import { useEffect, useRef, type ReactNode, type ComponentProps } from 'react';
import { createPortal } from 'react-dom';
import { noop } from 'lodash/fp';
import classnames from 'classnames/bind';
import { FocusTrap } from 'focus-trap-react';
import type { Placement } from '@floating-ui/react';
import { Theme } from 'app/lib/constants';
import { ThemeProvider } from 'app/components/core/ThemeProvider';
import { useDefaultId, useMousetrap, useOutsideModalClickHandler } from 'app/lib/hooks';
import type { ReferenceElement } from 'app/lib/utils';
import type { Position } from 'app/lib/types/ui';
import { useModalInliner } from './ModalInliner/ModalInliner';
import { useStoredDialogSize } from './useStoredDialogSize';
import { useAttachedModalLayout } from './useAttachedModalLayout';
import { ModalContext } from './ModalContext';
import type { ModalAttachment } from './types';
import * as styles from './Modal.css';

const cx = classnames.bind(styles);

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

export interface ModalProps extends Omit<ComponentProps<'div'>, 'id' | 'children'> {
    /**
     * Unique ID of the modal. This is used to wire up the aria properties to ensure an
     * accessible experience. An ID will be auto-generated if one is not provided.
     */
    id?: string;
    children?: ReactNode;
    testId?: string;
    onClose?: () => void;
    width?: number | string;
    height?: number | string;
    attachTo?: ModalAttachment;
    placement?: Placement;
    /**
     * Override the container used to display the modal. By default, <Modal> looks for an
     * element with the id `modal-container` in the document.
     */
    container?: HTMLElement;
    closeOnOutsideClick?: boolean;
    clickThroughPreviousModals?: boolean;
    showArrow?: boolean;
    theme?: Theme;
    className?: string;
    modalClassName?: string;
    initialFocus?: string;
    isResizable?: boolean;
    disableFocusTrap?: boolean;
    /**
     * Offsets the modal from the anchor element by the specified number of pixels
     * Overwrites the offset of the `showArrow` prop
     */
    offset?: number;
}

export function Modal({
    id,
    testId,
    children,
    onClose = noop,
    width,
    height,
    attachTo,
    placement = 'top',
    container,
    closeOnOutsideClick = false,
    clickThroughPreviousModals = true,
    initialFocus,
    showArrow = false,
    theme = Theme.LIGHT,
    className,
    modalClassName,
    isResizable = false,
    disableFocusTrap = false,
    offset,
    popover,
    ...props
}: ModalProps) {
    const isInlineModal = useModalInliner() || popover;
    const myId = useDefaultId('modal', id);
    const titleId = `${myId}-title`;
    const portalTarget = useRef(container || document.getElementById('modal-container')!);

    const { Arrow, context, elements, floatingStyles, refs } = useAttachedModalLayout({
        placement,
        attachTo: attachTo as ReferenceElement | Position,
        offset
    });

    const size = useStoredDialogSize(id, refs.floating, { width, height }, isResizable);

    const vertical = showArrow && (placement.startsWith('top') || placement.startsWith('bottom'));
    const horizontal = showArrow && (placement.startsWith('left') || placement.startsWith('right'));
    const rightViewPort = placement === 'right';
    const modalStyles = cx(
        'modal',
        theme,
        { showArrow, horizontal, vertical, rightViewPort, isResizable },
        modalClassName
    );
    const contentStyles = cx('content', className);

    useMousetrap('escape', onClose, false);
    useOutsideModalClickHandler(
        isInlineModal
            ? {
                  containerRef: refs.floating,
                  handler: onClose,
                  enabled: closeOnOutsideClick
              }
            : {
                  containerRef: portalTarget,
                  handler: onClose,
                  enabled: closeOnOutsideClick,

                  elementRef: refs.floating,
                  clickThroughPreviousSibling: clickThroughPreviousModals
              }
    );

    // For modals rendered as popovers, we need to manually make them visible
    useEffect(() => {
        if (popover && elements.floating) {
            try {
                elements.floating?.showPopover();
            } catch {
                // Ignore error. We might be trying to call this on an element
                // that's being removed from the DOM.
            }
        }
    }, [popover, elements.floating]);

    const modal = (
        <ThemeProvider theme={theme}>
            <ModalContext.Provider value={{ titleId, onClose }}>
                <div
                    id={myId}
                    role="dialog"
                    aria-labelledby={titleId}
                    tabIndex={-1}
                    className={modalStyles}
                    style={floatingStyles}
                    data-testid={testId ?? id}
                    onClick={stopPropagation}
                    popover={popover}
                    {...props}
                    ref={refs.setFloating}>
                    {showArrow && <Arrow ref={refs.arrow} context={context} />}
                    <FocusTrap
                        active={!disableFocusTrap && !isInlineModal}
                        focusTrapOptions={{
                            fallbackFocus: `#${myId}`,
                            escapeDeactivates: false,
                            clickOutsideDeactivates: false,
                            allowOutsideClick: () => true,
                            initialFocus
                        }}>
                        <div
                            className={contentStyles}
                            style={{
                                width: size.width,
                                height: size.height,
                                minWidth: width,
                                minHeight: height
                            }}>
                            {children}
                        </div>
                    </FocusTrap>
                </div>
            </ModalContext.Provider>
        </ThemeProvider>
    );

    return isInlineModal ? modal : createPortal(modal, portalTarget.current!);
}
